import { CameraPhotos, CaptureType } from '@EcamModel/model';
import { CameraViewType } from '@EcamModel/model/CameraView';
import { appConfig } from '@configs/index';
import { Box, Stack, Typography } from '@mui/material';
import color from '@theme/Colors';
import { formatDateByTimeZone } from 'src/helpers';
import React, { useRef, useState } from 'react';
export type CameraPhotosCarMovements = {
    DevMac: string;
    BlobName: string;
    Plate: string;
    CreatedAt: Date;
    TransactionId: number;
};

type Props = { photo: CameraPhotos; onClick?(photo: CameraPhotos): void; timezone?: string };

export default function BaseItemCard({ photo, onClick, ...props }: Props) {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const [overlayPos, setOverlayPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const margin = 12;
        const clientX = e.clientX;
        const clientY = e.clientY;
        const overlayEl = overlayRef.current;
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;
        const size = overlayEl ? overlayEl.getBoundingClientRect() : ({ width: 0, height: 0 } as DOMRect);

        let top = clientY + margin;
        let left = clientX + margin;

        if (top + size.height + margin > viewportH) {
            top = Math.max(margin, clientY - size.height - margin);
        }

        if (left + size.width + margin > viewportW) {
            left = Math.max(margin, viewportW - size.width - margin);
        }

        setOverlayPos({ top, left });
    };

    return (
        <Stack
            width="100%"
            justifyContent="center"
            alignItems="flex-start"
            spacing={1}
            sx={{ position: 'relative' }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onMouseMove={handleMouseMove}
        >
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '16/9',
                    background: `url(${appConfig.gateway.blobURL}${photo.BlobName}) no-repeat`,
                    backgroundPosition: 'center',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: 1,
                    cursor: 'pointer',
                }}
                onClick={() => onClick?.(photo)}
            />
            {/* <Typography variant="h5">
        Plate: {photo.Plate || "Unable to recognize license plate."}
    </Typography> */}
            {!isHovering && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '40px',
                        background:
                            'linear-gradient(to top, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
                        pointerEvents: 'none',
                        transition: 'opacity 0.3s ease',
                    }}
                />
            )}
            <Stack sx={{}}>
                <Typography variant="caption">
                    Plate:{' '}
                    {photo.CameraPhotoVehicles?.map((c) => c.Plate).join(', ') || 'Unable to recognize license plate'}
                </Typography>
                <Typography variant="caption">
                    On date:{' '}
                    {formatDateByTimeZone(photo.CaptureAt || 0, props.timezone)}
                </Typography>
                <Typography variant="caption">
                    Capture type: {photo.CaptureType === CaptureType.trigged ? 'Trigged' : 'Command'}
                </Typography>
            </Stack>

            {isHovering && (
                <Box
                    ref={overlayRef}
                    sx={{
                        position: 'fixed',
                        top: overlayPos.top,
                        left: overlayPos.left,
                        zIndex: (theme) => theme.zIndex.tooltip + 1,
                        bgcolor: color.white,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        boxShadow: 3,
                    }}
                >
                    <Stack spacing={0.25}>
                        {photo.CameraPhotoVehicles && photo.CameraPhotoVehicles.length > 0 ? (
                            photo.CameraPhotoVehicles.map((vehicle, index) => {
                                const infoParts: string[] = [];
                                if (vehicle.PlateColor) infoParts.push(`Plate color: ${vehicle.PlateColor}`);
                                if (vehicle.VehicleMake) infoParts.push(`Make: ${vehicle.VehicleMake}`);
                                if (vehicle.VehicleModel) infoParts.push(`Model: ${vehicle.VehicleModel}`);
                                if (vehicle.VehicleColor) infoParts.push(`Color: ${vehicle.VehicleColor}`);
                                return (
                                    <React.Fragment key={index}>
                                        <Typography variant="caption" fontWeight={500}>Plate: {vehicle.Plate}</Typography>
                                        {infoParts.length > 0 && (
                                            <Typography
                                                variant="caption"
                                                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            >
                                                {infoParts.join(' • ')}
                                            </Typography>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <Typography variant="caption">Plate: Unable to recognize license plate</Typography>
                        )}
                        <Typography variant="caption">
                            On date:{' '}
                            {formatDateByTimeZone(photo.CaptureAt || 0, props.timezone)}
                        </Typography>
                        <Typography variant="caption">
                            Capture type: {photo.CaptureType === CaptureType.trigged ? 'Trigged' : 'Command'}
                        </Typography>
                        <Typography variant="caption">
                            {`Within operational hours: ${photo.IsWithinOperationalHours}`}
                        </Typography>
                        <Typography variant="caption">
                            View type:{' '}
                            {photo.CameraView?.ViewType === CameraViewType.ViewOverview ? 'Overview' : 'Detail'}
                        </Typography>
                    </Stack>
                </Box>
            )}
        </Stack>
    );
}
